import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  Target,
  Award,
  Users,
  Share2,
  Gift,
  Star,
  CheckCircle,
  ArrowRight,
  Copy,
  MessageSquare,
  Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/doslions-logo.png";
const getToken = () => localStorage.getItem("token");

export default function Results() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const referralLink = `${window.location.origin}/?ref=${user?.id?.slice(0, 8) || 'friend'}`;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/user/stats`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setStats(response.data);
    } catch (error) {
      // Use default stats if endpoint not ready
      setStats({
        watchlist_count: 0,
        ai_queries: 0,
        days_active: 1,
        insights_received: 0,
        achievements: [],
        rank: "Beginner Trader"
      });
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToSocial = (platform) => {
    const text = "I'm using ECOLIONS AI Markets to save time on project research with LIONAI! Join me:";
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + referralLink)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralLink)}`
    };
    window.open(urls[platform], '_blank');
  };

  const achievements = [
    { id: "first_watchlist", name: "First Watch", desc: "Added first item to watchlist", icon: <Star className="w-5 h-5" />, unlocked: (stats?.watchlist_count || 0) >= 1 },
    { id: "ai_explorer", name: "AI Explorer", desc: "Asked LIONAI 10 questions", icon: <MessageSquare className="w-5 h-5" />, unlocked: (stats?.ai_queries || 0) >= 10 },
    { id: "week_streak", name: "Week Warrior", desc: "Active for 7 days", icon: <Zap className="w-5 h-5" />, unlocked: (stats?.days_active || 0) >= 7 },
    { id: "watchlist_pro", name: "Watchlist Pro", desc: "Track 10+ assets", icon: <Target className="w-5 h-5" />, unlocked: (stats?.watchlist_count || 0) >= 10 },
    { id: "referral_hero", name: "Referral Hero", desc: "Invited a friend", icon: <Users className="w-5 h-5" />, unlocked: false },
    { id: "power_trader", name: "Power Trader", desc: "Asked 50 LIONAI questions", icon: <Award className="w-5 h-5" />, unlocked: (stats?.ai_queries || 0) >= 50 }
  ];

  const successStories = [
    { name: "Carlos M.", gain: "+15%", quote: "LIONAI helped me understand NVDA before the AI boom", avatar: "C" },
    { name: "Maria L.", gain: "+22%", quote: "The gold analysis was spot on during inflation fears", avatar: "M" },
    { name: "James R.", gain: "+8%", quote: "Personalized scenarios match my risk tolerance perfectly", avatar: "J" }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] pb-20 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div 
              className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate("/dashboard")}
              data-testid="logo-home-link"
            >
              <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                <span className="text-amber-500">ECOLIONS</span> RESULTS
              </span>
            </div>
            <Button 
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="text-zinc-400 hover:text-amber-500"
            >
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Your Progress */}
        <section className="bg-[#111111] border border-zinc-800 p-6">
          <h2 className="text-2xl font-bold tracking-tight uppercase mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
            Your <span className="text-amber-500">Progress</span>
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-[#0A0A0A] border border-zinc-800 p-4 text-center">
              <p className="text-3xl font-bold font-data text-amber-500">{stats?.watchlist_count || 0}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Watching</p>
            </div>
            <div className="bg-[#0A0A0A] border border-zinc-800 p-4 text-center">
              <p className="text-3xl font-bold font-data text-emerald-500">{stats?.ai_queries || 0}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">AI Queries</p>
            </div>
            <div className="bg-[#0A0A0A] border border-zinc-800 p-4 text-center">
              <p className="text-3xl font-bold font-data text-white">{stats?.days_active || 1}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Days Active</p>
            </div>
            <div className="bg-[#0A0A0A] border border-zinc-800 p-4 text-center">
              <p className="text-3xl font-bold font-data text-blue-500">{stats?.insights_received || 0}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">Insights</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30">
            <Award className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Your Rank</p>
              <p className="font-bold text-amber-500">{stats?.rank || "Beginner Trader"}</p>
            </div>
          </div>
        </section>

        {/* Achievements */}
        <section className="bg-[#111111] border border-zinc-800 p-6">
          <h2 className="text-2xl font-bold tracking-tight uppercase mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
            <Award className="w-6 h-6 inline mr-2 text-amber-500" />
            Achievements
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {achievements.map((achievement) => (
              <div 
                key={achievement.id}
                className={`p-4 border ${achievement.unlocked ? 'bg-amber-500/10 border-amber-500/30' : 'bg-zinc-900/50 border-zinc-800 opacity-50'}`}
              >
                <div className={`w-10 h-10 flex items-center justify-center mb-3 ${achievement.unlocked ? 'text-amber-500 bg-amber-500/20' : 'text-zinc-600 bg-zinc-800'}`}>
                  {achievement.icon}
                </div>
                <p className="font-semibold text-sm">{achievement.name}</p>
                <p className="text-xs text-zinc-500 mt-1">{achievement.desc}</p>
                {achievement.unlocked && (
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-2" />
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Referral Program */}
        <section className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Gift className="w-8 h-8 text-amber-500" />
            <div>
              <h2 className="text-2xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                Invite Friends, <span className="text-amber-500">Earn Rewards</span>
              </h2>
              <p className="text-sm text-zinc-400">Give $3 off, get 1 month free for each friend who subscribes</p>
            </div>
          </div>

          <div className="bg-[#0A0A0A] border border-zinc-800 p-4 mb-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Your Referral Link</p>
            <div className="flex items-center gap-2">
              <input 
                type="text" 
                value={referralLink}
                readOnly
                className="flex-1 bg-transparent text-sm font-mono text-zinc-300 outline-none"
              />
              <Button 
                onClick={copyReferralLink}
                className={`${copied ? 'bg-emerald-500' : 'bg-amber-500 hover:bg-amber-600'} text-black`}
                size="sm"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={() => shareToSocial('twitter')} variant="outline" size="sm" className="border-zinc-700">
              Share on X
            </Button>
            <Button onClick={() => shareToSocial('facebook')} variant="outline" size="sm" className="border-zinc-700">
              Facebook
            </Button>
            <Button onClick={() => shareToSocial('whatsapp')} variant="outline" size="sm" className="border-zinc-700">
              WhatsApp
            </Button>
            <Button onClick={() => shareToSocial('linkedin')} variant="outline" size="sm" className="border-zinc-700">
              LinkedIn
            </Button>
          </div>
        </section>

        {/* Success Stories */}
        <section className="bg-[#111111] border border-zinc-800 p-6">
          <h2 className="text-2xl font-bold tracking-tight uppercase mb-6" style={{ fontFamily: 'Barlow Condensed' }}>
            <Users className="w-6 h-6 inline mr-2 text-amber-500" />
            Community <span className="text-amber-500">Success</span>
          </h2>
          
          <div className="space-y-4">
            {successStories.map((story, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-[#0A0A0A] border border-zinc-800">
                <div className="w-12 h-12 bg-amber-500/20 flex items-center justify-center text-amber-500 font-bold flex-shrink-0">
                  {story.avatar}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold">{story.name}</p>
                    <span className="text-xs bg-emerald-500/20 text-emerald-500 px-2 py-0.5">{story.gain} portfolio</span>
                  </div>
                  <p className="text-sm text-zinc-400">"{story.quote}"</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-zinc-600 mt-4 text-center">
            Results vary. Past performance doesn't guarantee future results. Not financial advice.
          </p>
        </section>

        {/* CTA */}
        {!user?.is_subscribed && (
          <section className="bg-amber-500 p-6 text-center">
            <h2 className="text-2xl font-bold tracking-tight uppercase mb-2 text-black" style={{ fontFamily: 'Barlow Condensed' }}>
              Ready to Get Results?
            </h2>
            <p className="text-black/70 mb-4">
              Join successful traders using LIONAI
            </p>
            <Button 
              onClick={() => navigate("/subscription")}
              className="bg-black hover:bg-zinc-900 text-amber-500 font-bold uppercase tracking-wider"
            >
              Unlock LIONAI - $3 First Month <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </section>
        )}
      </main>
    </div>
  );
}
